import React, { useState, useCallback, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { usePostHog } from "../../hooks/usePostHog";
// import PhoneInput from "react-phone-number-input";
import {
  parsePhoneNumberFromString,
  getCountryCallingCode,
} from "libphonenumber-js";
import { useSignupMutation } from "../../services/apiSlice";
import Button from "../../components/common/Button/Button";

import LoadingCommon from "../../components/common/LoadingStatus/LoadingCommon";
import images from "../../images";
import DataProtectionPolicyModal from "../../components/common/Drawer/DataProtectionPolicyModal";
import CustomInputProps from "../../components/CustomInputProps/CustomInputProps";
import { CheckboxCommon } from "../../components/common/CheckBox/CheckboxCommon";

// Constants
// const DEFAULT_COUNTRY = "SG";
const DEFAULT_COUNTRY_CODE = "+65";

const TOAST_MESSAGES = {
  OTP_SUCCESS: "toast.success.otpCodeWillExprireIn5Minutes",
  SIGNUP_FAILED: "signUp.signUpFailed",
  INVALID_PHONE: "signUp.invalidPhoneNumber",
  INVALID_AGE: "signUp.invalidAge",
  INVALID_GENDER: "signUp.invalidGender",
  AGE_OUT_OF_RANGE: "signUp.ageOutOfRange",
  VERIFYING_OTP: "toast.warning.accIsVerifyingOTPTryAgainTomorrow",
  UNEXPECTED_ERROR: "toast.error.anUnexpectedErrorOccurred",
};

// Utility Functions
const parseQueryParams = (search) => {
  const params = new URLSearchParams(search);
  return {
    questId: params.get("quest_id"),
    locationId: params.get("location_id"),
  };
};

const parsePhoneNumber = (phone) => {
  try {
    const phoneFirst8 = phone.slice(0, 8);

    const phoneWithCountryCode = phoneFirst8.startsWith("+")
      ? phoneFirst8
      : `${DEFAULT_COUNTRY_CODE}${phoneFirst8}`;

    const phoneNumber = parsePhoneNumberFromString(phoneWithCountryCode);
    if (phoneNumber && phoneNumber.isValid()) {
      const country = phoneNumber.country;
      const countryCode = country ? `+${getCountryCallingCode(country)}` : "";
      const phoneWithoutCountryCode = phoneFirst8;
      return { countryCode, phoneWithoutCountryCode, isValid: true };
    }
    return { countryCode: "", phoneWithoutCountryCode: "", isValid: false };
  } catch {
    return { countryCode: "", phoneWithoutCountryCode: "", isValid: false };
  }
};

const handleApiError = (error, t) => {
  const status = error?.status || error?.response?.status;
  // const errorMsg =
  //   error?.data?.detail?.[0]?.msg ||
  //   error?.response?.data?.detail?.[0]?.msg ||
  //   t(TOAST_MESSAGES.SIGNUP_FAILED);

  if (status === 400) {
    return t(TOAST_MESSAGES.VERIFYING_OTP);
  }
  if (status === 422) {
    // return errorMsg;
    return (
      <span>
        {t("signUp.yourNumberHasBeenRegistered")}{" "}
        <Link
          to="/login"
          className="text-[#EF4056] font-bold transition-colors lowercase"
        >
          {t("signUp.signIn")}
        </Link>
      </span>
    );
  }
  return t(TOAST_MESSAGES.UNEXPECTED_ERROR);
};

// Components
const SignUpHeader = () => {
  const { t } = useTranslation();
  return (
    <header className="text-left">
      <h1 className="text-2xl font-bold text-[#253FF6]">
        {t("signUp.signUp")}
      </h1>
    </header>
  );
};

const SignUpForm = ({
  signUpData,
  setSignUpData,
  onSubmit,
  isLoading,
  questId,
  locationId,
  error,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handlePhoneChange = useCallback(
    (value) => {
      const rawPhone =
        typeof value === "string" ? value.replace(/[^0-9]/g, "") : "";

      const phoneFirst8Digits = rawPhone.slice(0, 8);

      const formattedPhone =
        phoneFirst8Digits.length > 4
          ? `${phoneFirst8Digits.slice(0, 4)}-${phoneFirst8Digits.slice(4, 8)}`
          : phoneFirst8Digits;

      const parsed = parsePhoneNumber(phoneFirst8Digits);
      setSignUpData({
        ...signUpData,
        phoneNumber: formattedPhone,
        rawPhoneNumber: phoneFirst8Digits,
        countryCode: parsed.countryCode,
        phoneWithoutCountryCode: parsed.phoneWithoutCountryCode,
        isPhoneValid: parsed.isValid,
      });
    },
    [setSignUpData, signUpData]
  );

  const handlePrivacyPolicyChange = useCallback(
    (event) => {
      setSignUpData({
        ...signUpData,
        // privacyPolicyAccepted: event.target.checked,
        privacyPolicyAccepted: event,
      });
    },
    [setSignUpData, signUpData]
  );

  const handleConsentMarketingChange = useCallback(
    (event) => {
      setSignUpData({
        ...signUpData,
        // privacyPolicyAccepted: event.target.checked,
        consentMarketing: event,
      });
    },
    [setSignUpData, signUpData]
  );

  const handleToSignIn = () => {
    if (questId || locationId) {
      navigate(
        `/sign-in?from=qrcode&quest_id=${questId}&location_id=${locationId}`
      );
    } else {
      navigate("/sign-in");
    }
  };

  const handleAgree = () => {
    setSignUpData({ ...signUpData, privacyPolicyAccepted: true });
    setIsModalOpen(false);
  };

  const isFormValid =
    signUpData.isPhoneValid &&
    // signUpData.isAgeValid &&
    // !!signUpData.gender &&
    signUpData.privacyPolicyAccepted;

  return (
    <div className="flex flex-col mt-4 max-w-[340px] text-sm text-[#A0A0A0] gap-6">
      <label htmlFor="phone-input" className="sr-only">
        {t("signUp.phoneNumber")}
      </label>

      <div>
        {/* <PhoneInputProps
          value={signUpData.phoneNumber}
          onChange={handlePhoneChange}
        /> */}
        <CustomInputProps
          value={signUpData.phoneNumber}
          onChange={handlePhoneChange}
          placeholder={t("signUp.whatsAppNumber")}
          iconImage={images.WhatsappIconWhite}
          countryCode={signUpData.countryCode || DEFAULT_COUNTRY_CODE}
          iconDropdown={images.DropPhoneIconGray}
        />
        {error.phone && (
          <p className="text-[#EF4056] text-sm  mt-2">{error.phone}</p>
        )}
      </div>

      <div className="flex items-center">
        <CheckboxCommon
          checked={signUpData.consentMarketing || false}
          onCheckedChange={handleConsentMarketingChange}
          id="consentToRallyCollecting-privacy-policy"
          className="rounded-[2px] w-4 h-4 bg-white border-2 border-[#A0A0A0] data-[state=checked]:bg-white data-[state=checked]:text-[#A0A0A0]"
        />
        <label
          htmlFor="consentToRallyCollecting-privacy-policy"
          className="ml-2 block text-xs text-[#A0A0A0]"
        >
          <span>{t("signUp.consentToRallyCollecting")} </span>
        </label>
      </div>
      <div>
        <div className="flex items-start">
          <CheckboxCommon
            id="privacy-policy"
            checked={signUpData.privacyPolicyAccepted || false}
            onCheckedChange={handlePrivacyPolicyChange}
            className="mt-1 rounded-[2px] w-4 h-4 bg-white border-2 border-[#A0A0A0] data-[state=checked]:bg-white data-[state=checked]:text-[#A0A0A0]"
          />
          <label
            htmlFor="privacy-policy"
            className="ml-2 block text-xs text-[#A0A0A0]"
          >
            <Trans i18nKey="signUp.iHaveReadAndhearby">
              <DataProtectionPolicyModal
                isOpen={isModalOpen}
                onOpenChange={setIsModalOpen}
                onAgree={handleAgree}
                onClose={() => setIsModalOpen(false)}
              />
            </Trans>
            {error.privacyPolicy && (
              <p className="text-[#EF4056] text-sm">{error.privacyPolicy}</p>
            )}
          </label>
        </div>
      </div>

      <Button
        className="px-[18px] py-3 text-[20px] font-medium text-white bg-[#253FF6] rounded-md max-w-[368px] w-full h-[50px]"
        text={t("signUp.getWhatsAppOTP")}
        iconSub={images.WhatsappIconWhite}
        onClick={onSubmit}
        disabled={isLoading || !isFormValid}
        aria-label={t("signUp.getWhatsAppOTP")}
      />
      <p className=" text-sm">
        <span className="text-[#A0A0A0]">{t("signUp.alreadyHaveAccount")}</span>{" "}
        <button
          onClick={handleToSignIn}
          className="text-[#253FF6] font-bold hover:underline"
          aria-label={t("base.loginHaveSpace")}
        >
          {t("base.loginHaveSpace")}
        </button>
      </p>
      {/* <PrivacyPolicyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAgree={handleAgree}
      /> */}
    </div>
  );
};

// const SignUpFooter = () => <Copyright />;
const SignUpFooter = () => null;

// Main Component
const SignUpPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [register, { isLoading }] = useSignupMutation();
  const { trackEvent, trackTimer } = usePostHog();

  const [error, setError] = useState({
    phone: "",
    privacyPolicy: "",
    isPhoneValid: false,
  });
  const [signUpData, setSignUpData] = useState({
    phoneNumber: "",
    rawPhoneNumber: "",
    countryCode: DEFAULT_COUNTRY_CODE,
    phoneWithoutCountryCode: "",
    age: "",
    gender: "",
    isPhoneValid: false,
    isAgeValid: false,
    privacyPolicyAccepted: false,
    consentMarketing: false,
  });
  const { questId, locationId } = parseQueryParams(useLocation().search);

  // Track page view on component mount
  useEffect(() => {
    trackEvent('registration_page_viewed', {
      referral_source: questId ? 'quest_discovery' : 'organic',
      has_quest_context: !!questId,
      has_location_context: !!locationId,
      quest_id: questId,
      location_id: locationId
    });
  }, [trackEvent, questId, locationId]);

  const validateForm = (signUpData, t) => {
    const errors = {};

    if (!signUpData.isPhoneValid) {
      errors.phone = t(TOAST_MESSAGES.INVALID_PHONE);
    }

    if (!signUpData.privacyPolicyAccepted) {
      errors.privacyPolicy = t("base.required");
    }

    return errors;
  };

  const handleSubmit = useCallback(() => {
    const errors = validateForm(signUpData, t);

    if (Object.keys(errors).length > 0) {
      setError(errors);
      trackEvent('registration_validation_failed', {
        errors: Object.keys(errors),
        phone_valid: signUpData.isPhoneValid,
        privacy_accepted: signUpData.privacyPolicyAccepted,
        consent_marketing: signUpData.consentMarketing
      });
      return;
    }

    // Track registration attempt
    const registrationTimer = trackTimer('registration_duration');
    trackEvent('registration_started', {
      phone_country_code: signUpData.countryCode,
      referral_source: questId ? 'quest_discovery' : 'organic',
      has_quest_context: !!questId,
      has_location_context: !!locationId,
      consent_marketing: signUpData.consentMarketing,
      consent_data_protection: signUpData.privacyPolicyAccepted
    });

    setError({
      phone: "",
      age: "",
      gender: "",
      privacyPolicy: "",
    });
    const body = {
      phone: signUpData.rawPhoneNumber,
      country_code: signUpData.countryCode,
      // age: Number(signUpData.age),
      // gender: signUpData.gender,
      // age: 24,
      // gender: "male",
      consent_marketing: signUpData.consentMarketing,
      consent_data_protection: signUpData.privacyPolicyAccepted,
    };

    register(body)
      .unwrap()
      .then((response) => {
        if (
          response.message === "Operation successful" ||
          response.status === 200
        ) {
          registrationTimer.end({
            success: true,
            phone_country_code: signUpData.countryCode
          });
          
          trackEvent('registration_success', {
            phone_country_code: signUpData.countryCode,
            referral_source: questId ? 'quest_discovery' : 'organic',
            consent_marketing: signUpData.consentMarketing
          });

          toast.success(t(TOAST_MESSAGES.OTP_SUCCESS));
          navigate(
            `/verify-otp?phone=${signUpData.phoneWithoutCountryCode}&country_code=${signUpData.countryCode}&quest_id=${questId}&location_id=${locationId}&type=signup`
          );
        }
      })
      .catch((error) => {
        registrationTimer.end({
          success: false,
          error_code: error.status,
          error_message: error.data?.detail?.[0]?.msg || error.message
        });
        
        trackEvent('registration_failed', {
          error_code: error.status,
          error_type: error.status === 422 ? 'already_registered' : 'api_error',
          phone_country_code: signUpData.countryCode,
          referral_source: questId ? 'quest_discovery' : 'organic'
        });

        // toast.error(handleApiError(error, t));
        setError((prev) => ({
          ...prev,
          phone: handleApiError(error, t),
        }));
      });
  }, [signUpData, questId, locationId, register, navigate, t, trackEvent, trackTimer]);

  return (
    <div className="flex  flex-col justify-evenly px-11 w-full bg-white h-full  ">
      <Helmet>
        <meta name="theme-color" content="#fefce8" />
      </Helmet>
      <LoadingCommon statusLoading={isLoading} />
      <div className="flex items-center w-full pt-[155px]">
        <img
          src={images.LogoLogin}
          className="w-[110px] h-[81.95px] aspect-[110/81.95]"
          alt=""
        />
      </div>
      <div className="flex flex-col gap-6 justify-between mt-[60px]  w-full bg-white ">
        <div className="w-full">
          <SignUpHeader />
          <SignUpForm
            signUpData={signUpData}
            setSignUpData={setSignUpData}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            questId={questId}
            locationId={locationId}
            error={error}
          />
        </div>
        <SignUpFooter />
      </div>
      <div></div>
      <div></div>
      <div></div>
    </div>
  );
};

export default SignUpPage;
